import React, { useMemo, useState } from 'react';
import { Stack, Typography, Box } from '@mui/material';
import { Button } from 'system/atoms';
import { TableV3, TableV3ColDefs, TableV3Sort } from 'common/table';
import {
  useGetAttachmentsByCandidateProfileQuery,
  useGetMediaDownloadUrlLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { formatDateFromNow } from 'utils/datetime';
import {
  formatBytes,
  getFileTypeFromFileName
} from 'core/services/storage/utils';
import { FilesViewer } from 'components/FileViewer';
import { downloadFileFromUrl } from 'core/services/storage/utils';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';

interface DocumentRow {
  id: string;
  title: string;
  added: string;
  fileType: string;
  size: string;
  fileName: string;
}

export const CandidateProfileDocumentsTab: React.FC = () => {
  const { getPathParams } = usePathsV2();
  const { candidateProfileId } = getPathParams.CANDIDATE_PROFILE_DETAILS();

  const [sort, setSort] = useState<TableV3Sort<string>>({
    field: 'added',
    direction: 'desc'
  });
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  );
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  const { data, loading } = useGetAttachmentsByCandidateProfileQuery({
    variables: {
      candidateProfileId
    }
  });

  const [getMediaDownloadUrl] = useGetMediaDownloadUrlLazyQuery();

  const attachments = extractQueryListItems(data);

  const documents = useMemo<DocumentRow[]>(() => {
    if (!attachments) return [];

    return attachments.map((attachment) => {
      const fileType =
        getFileTypeFromFileName(attachment.media.fileName) ||
        attachment.media.contentType.split('/')[1]?.toUpperCase() ||
        'FILE';

      return {
        id: attachment.id,
        title: attachment.media.fileName,
        added: formatDateFromNow(attachment.createdAt),
        fileType: fileType.toUpperCase(),
        size: attachment.media.size ? formatBytes(attachment.media.size) : '-',
        fileName: attachment.media.fileName
      };
    });
  }, [attachments]);

  const selectedDocument = useMemo(() => {
    return attachments?.find((a) => a.id === selectedDocumentId);
  }, [attachments, selectedDocumentId]);

  const handleViewDocument = async (attachmentId: string) => {
    setSelectedDocumentId(attachmentId);

    const { data } = await getMediaDownloadUrl({
      variables: {
        attachmentId
      }
    });

    if (data?.getMediaDownloadUrl?.url) {
      setDocumentUrl(data.getMediaDownloadUrl.url);
    }
  };

  const handleDownloadDocument = async (
    attachmentId: string,
    fileName: string
  ) => {
    const { data } = await getMediaDownloadUrl({
      variables: {
        attachmentId
      }
    });

    if (data?.getMediaDownloadUrl?.url) {
      downloadFileFromUrl(data.getMediaDownloadUrl.url, fileName);
    }
  };

  const colDefs: TableV3ColDefs<DocumentRow, string> = [
    {
      field: 'title',
      width: 400,
      sortField: 'title',
      searchable: true,
      searchPlaceholder: 'Search document titles...',
      headerName: 'Document title',
      cellRenderer: ({ rowData }: { rowData: DocumentRow }) => (
        <Typography
          variant="body2"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          onClick={() => handleViewDocument(rowData.id)}
        >
          {rowData.title}
        </Typography>
      )
    },
    {
      field: 'added',
      width: 150,
      headerName: 'Added',
      sortField: 'added'
    },
    {
      field: 'fileType',
      width: 120,
      headerName: 'File type'
    },
    {
      field: 'size',
      width: 120,
      headerName: 'Size'
    },
    {
      field: 'id',
      width: 120,
      headerName: '',
      cellRenderer: ({ rowData }: { rowData: DocumentRow }) => (
        <Button
          variant="tertiary"
          onClick={() => handleDownloadDocument(rowData.id, rowData.fileName)}
        >
          Download
        </Button>
      )
    }
  ];

  return (
    <Stack direction="row" gap={3} height="100%">
      <Box flex={1}>
        <TableV3
          tableId="candidate-profile-documents"
          columns={colDefs}
          data={documents}
          defaultSort={sort}
          onSort={setSort}
          loading={loading}
        />
      </Box>
      {selectedDocument && documentUrl && (
        <Box
          flex={1}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            height: '949px',
            overflow: 'auto'
          }}
        >
          <Stack gap={2} height="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">
                {selectedDocument.media.fileName}
              </Typography>
              <Button
                variant="secondary"
                onClick={() =>
                  handleDownloadDocument(
                    selectedDocument.id,
                    selectedDocument.media.fileName
                  )
                }
              >
                Download
              </Button>
            </Stack>
            <Box flex={1}>
              <FilesViewer
                docs={[
                  {
                    uri: documentUrl,
                    fileName: selectedDocument.media.fileName,
                    fileType: getFileTypeFromFileName(
                      selectedDocument.media.fileName
                    )
                  }
                ]}
                style={{
                  height: '100%',
                  width: '100%'
                }}
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
